<script setup lang="ts">
import { ref, watch } from 'vue';
import LoaderButton from '@/components/display/button/LoaderButton.vue';

interface Props {
  title?: string;
  titleIcon?: string;
  buttonIcon?: string;
  buttonText?: string;
  buttonAction?: (e: Event) => void;
  buttonActionLoading?: string;
  tooltip?: string;
  small?: boolean;
  UkButtonIcon?: string;
  medium?: boolean;
  headerInput?: string;
  input?: string;
  btnDisable?: boolean;
  closeButton?: boolean;
  buttonDataTid?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'inputChange', value: string): void;
  (e: 'closeImportModal'): void;
}>();

const headerInputValue = ref(props.input ?? '');

watch(
  () => props.input,
  val => {
    headerInputValue.value = val ?? '';
  }
);

function onHeaderInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  headerInputValue.value = value;
  emit('inputChange', value);
}

function onClose() {
  emit('closeImportModal');
}
</script>

<template>
  <div
    class="uk-flex uk-flex-middle uk-flex-between heading-custom-container uk-background-tertiary uk-border-rounded-top"
    :class="{
      'team-roster-head': headerInput,
      'head-padding-close': closeButton,
    }"
  >
    <!-- display title or input -->
    <div v-if="!headerInput" class="uk-flex uk-flex-middle">
      <h2 class="uk-text-bold uk-text-size uk-margin-remove-bottom text-medium">
        <div
          v-if="titleIcon"
          :uk-icon="`icon:${titleIcon}; ratio:3`"
          class="uk-flex-grow uk-margin-small-bottom"
        />
        <span
          :class="{
            'uk-text-small': small,
            'text-medium': medium,
          }"
        >
          {{ title }}
        </span>
      </h2>
    </div>
    <input
      v-else
      v-model="headerInputValue"
      @input="onHeaderInput"
      type="text"
      class="uk-input uk-margin-right uk-width-2-3"
      :placeholder="headerInput"
    />

    <!-- action buttons -->
    <div class="uk-flex uk-flex-middle">
      <LoaderButton
        v-if="buttonAction"
        class="heading-action-button uk-flex uk-flex-center uk-flex-middle uk-button uk-button-primary uk-padding-small"
        data-cy="heading-loader-button"
        :data-tid="buttonDataTid || 'add-new-button'"
        :disabled="btnDisable"
        :loaderStatus="buttonActionLoading"
        :uk-tooltip="`title: ${tooltip}; pos: left;`"
        @click="buttonAction($event)"
      >
        <i v-if="UkButtonIcon" :uk-icon="`icon:${UkButtonIcon}; ratio:1`" />
        <img v-if="buttonIcon" :src="`/icons/white/icon-white-${buttonIcon}.svg`" />
        <span
          v-if="buttonText && buttonText.trim() !== ''"
          class="uk-text-bold uk-margin-small-left"
        >
          {{ buttonText }}
        </span>
      </LoaderButton>

      <button
        v-if="closeButton"
        class="close-custom-margin"
        uk-icon="icon: close;ratio:1.3"
        type="button"
        @click="onClose"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.heading-action-button {
  z-index: 1;
  border-radius: 4px;

  i {
    flex-shrink: 0;
  }

  img {
    height: 1rem;
    width: 1.375rem;
    flex-shrink: 0;
  }

  span {
    line-height: 1;
  }
}

.close-custom-margin {
  padding: 10px 19px;
}

.text-medium {
  font-size: $global-large-font-size;
}

.btn-disable {
  opacity: 0.5;
}

.heading-custom-container {
  padding: 15px;
  gap: 10px;
}

.head-padding-close {
  padding: 4px 0 4px 15px;
}

.team-roster-head {
  padding: 9px 0 9px 9px;
}

.uk-text-size {
  @media only screen and (max-width: 1024px) {
    font-size: $global-xlarge-font-size;
  }
}
</style>
